/** @jsxImportSource @emotion/react */
import { SerializedStyles } from "@emotion/react";
import { ProfileIcon } from "components/general/UserIcons/ProfileIcon/ProfileIcon";
import React, { useEffect } from "react";
import { useAccount } from "wagmi";
import { fetchEnsAvatar, fetchEnsName } from "wagmi/actions";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { MODAL_SELECTORS } from "../../Modal/instance/modalSelectors";
import "./ConnectedWalletButton.css";
import { GuildRole } from "./GuildRole";
import { WalletAddressLabel } from "./WalletAddressLabel";
import { useTokenListByChain } from "hooks/useTokenListByChain";

export enum WalletContentDisplayLocations {
  "WalletView" = "WalletView",
  "Header" = "Header",
  "Sidenav" = "Sidenav",
  "NavbarV2" = "NavbarV2",
  "NavbarV2Mobile" = "NavbarV2Mobile",
}

export interface WalletContentDisplayLocationsProps {
  displayLocation: WalletContentDisplayLocations;
  css?: SerializedStyles;
}

export type ButtonClassMap = Record<"button" | "content", string>;

export const ConnectedWalletButton: React.FC<WalletContentDisplayLocationsProps> = props => {
  const { isConnected, address } = useAccount();
  const tokenList = useTokenListByChain();
  const BUTTON_CLASSES: Record<Partial<WalletContentDisplayLocations>, ButtonClassMap> = {
    [WalletContentDisplayLocations.Sidenav]: {
      button: "wallet-btn hasRole d-flex align-items-center justify-content-center w-100 p-0",
      content: "ms-3",
    },
    [WalletContentDisplayLocations.Header]: {
      button:
        "wallet-btn wallet-btn-header-padding hasRole d-flex align-items-center justify-content-center flex-shrink-0",
      content: "ms-2 d-md-flex d-none",
    },
    [WalletContentDisplayLocations.WalletView]: { button: "", content: "" },
    [WalletContentDisplayLocations.NavbarV2]: { button: "", content: "" },
    [WalletContentDisplayLocations.NavbarV2Mobile]: { button: "", content: "" },
  };

  useEffect(() => {
    if (isConnected && address) {
      GlobalStore.update(s => {
        s.address = address;
      });
      const fetchEns = async () => {
        const ensName = await fetchEnsName({ address, chainId: 1 });
        if (ensName) {
          const ensAvatar = await fetchEnsAvatar({ name: ensName, chainId: 1 });
          GlobalStore.update(s => {
            s.ensName = ensName;
            s.ensAvatar = ensAvatar ?? "";
          });
        } else {
          GlobalStore.update(s => {
            s.ensName = "";
            s.ensAvatar = "";
          });
        }
      };
      fetchEns().catch(err => {
        console.error(`could not get ENS Details`, err);
      });
    }
  }, [isConnected, address, tokenList]);

  return (
    <button
      type="button"
      className={BUTTON_CLASSES[props.displayLocation].button}
      onClick={() => {
        GlobalStore.update(s => {
          s.modalSelector = MODAL_SELECTORS.WALLET_VIEW;
        });
      }}
      css={props.css}
    >
      <ProfileIcon borderRadius={"50%"} withBackground={true} />
      <div
        className={`flex-column align-items-start ` + BUTTON_CLASSES[props.displayLocation].content}
      >
        <WalletAddressLabel displayLocation={props.displayLocation} />
        <GuildRole displayLocation={props.displayLocation} />
      </div>
    </button>
  );
};
